import React, { useState } from "react";
import { IonModal, IonIcon, IonButton, IonSpinner } from "@ionic/react";
import { close } from "ionicons/icons";

// F-525 (UAT, URS_6.8): the buttons covered the pop-up's text on a phone and on the web build.
// The sheet was a fixed-height box with `overflow: hidden` whose button stack carried the global
// `.button-stack-container` class (`position: fixed` in theme/variables.css), so the layout
// depended on an inline override and a long message had nowhere to go. Now the sheet is a flex
// column: header, a message area that SCROLLS, and the buttons in normal flow as a footer that
// keeps its own height. Nothing is absolutely positioned over the text, and the text keeps its
// size. `max-height` is capped by the modal box too (`100% - margins`), because on the web build
// the IonModal is a 600×500 box, smaller than 70% of a desktop viewport.
// ShareDataChooser (the "Individual / Organization" choice) uses the same three styles.
export const POPUP_SHEET_STYLE: React.CSSProperties = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  margin: "20px",
  backgroundColor: "white",
  borderRadius: "20px",
  maxHeight: "min(70vh, calc(100% - 40px))",
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

export const POPUP_BODY_STYLE: React.CSSProperties = {
  flex: "1 1 auto",
  minHeight: 0,
  overflowY: "auto",
};

export const POPUP_ACTIONS_STYLE: React.CSSProperties = {
  position: "static",
  flexShrink: 0,
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  // The gap, not the buttons' own marginBottom: the theme forces `margin: 0 !important` on
  // .primary-button / .tertiary-button, so a per-button margin never applies.
  gap: "16px",
  padding: "16px 16px 24px",
};

interface ActionButton {
  text: string;
  action: () => Promise<void> | void;
  isPrimary?: boolean;
}

interface RevokeAccessModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  name?: string;
  message: string;
  actionButtons: ActionButton[];
  children?: React.ReactNode;
}

const ActionButtons: React.FC<RevokeAccessModalProps> = ({
  isOpen,
  onClose,
  title,
  name,
  message,
  actionButtons,
  children,
}) => {
  const [loadingIndex, setLoadingIndex] = useState<number | null>(null);

  const handleButtonClick = async (
    action: () => Promise<void> | void,
    index: number,
  ) => {
    setLoadingIndex(index);
    try {
      const result = action();
      if (result instanceof Promise) {
        await result;
      }
    } finally {
      setLoadingIndex(null);
    }
  };

  return (
    <IonModal
      isOpen={isOpen}
      onDidDismiss={onClose}
      presentingElement={document.querySelector("ion-app") || undefined}
      canDismiss={true}
      showBackdrop={true}
      backdropDismiss={true}
      style={{
        "--background": "transparent",
        "--box-shadow": "none",
        "--border-radius": "20px",
      }}
    >
      <div data-testid="action-popup-sheet" style={POPUP_SHEET_STYLE}>
        {/* Header with close button and title */}
        <div
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            margin: "16px",
          }}
        >
          <div style={{ width: "40px" }}></div>
          <h2
            style={{
              fontSize: "24px",
              fontWeight: "700",
              margin: "0",
              color: "#000",
              flex: 1,
              textAlign: "center",
            }}
          >
            {title}
          </h2>
          <button
            onClick={onClose}
            style={{
              background: "none",
              border: "none",
              color: "#999",
              cursor: "pointer",
              padding: "8px",
              width: "40px",
              height: "40px",
              display: "flex",
              alignItems: "top",
              justifyContent: "top",
            }}
          >
            <IonIcon icon={close} style={{ fontSize: "24px" }} />
          </button>
        </div>
        {/* Message — scrolls inside the sheet when it is taller than the space left. */}
        <div data-testid="action-popup-body" style={POPUP_BODY_STYLE}>
          {name && (
            <div className="text-sm font-semibold text-center mb-2 cursor-pointer p-4">
              {name}
            </div>
          )}
          {children ? children : null}
          <div className="flex text-xl text-center pt-0 p-4 items-center justify-center">
            {message}
          </div>
        </div>

        {/* Buttons — in normal flow below the message, never over it (F-525). */}
        <div data-testid="action-popup-actions" style={POPUP_ACTIONS_STYLE}>
          {actionButtons.map((button, index) => (
            <IonButton
              key={index}
              className={`${button.isPrimary ? "primary-button" : "tertiary-button"
              } !w-[275px]`}
              onClick={() => handleButtonClick(button.action, index)}
              fill={button.isPrimary ? "solid" : "outline"}
              style={{
                "--border-radius": "30px",
                marginBottom: index < actionButtons.length - 1 ? "16px" : "0",
                fontSize: "18px",
                fontWeight: "500",
                ...(button.isPrimary
                  ? {}
                  : { "--color": "#ffffff !important", borderRadius: "30px" }),
              }}
              disabled={loadingIndex !== null}
            >
              {loadingIndex === index ? (
                <IonSpinner name="dots" />
              ) : (
                button.text
              )}
            </IonButton>
          ))}
        </div>
      </div>
    </IonModal>
  );
};

export default ActionButtons;
